import { useState, useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useCountry,
  type Country,
} from "@/app/services/context/CountryContext";
import {
  gxAccountBankCurrencyApi,
  gxAccountBalanceTotalApi,
} from "@/app/apiConfig/services/accountsAbstractApiLayer";
import { useTranslation } from "react-i18next";
import {
  fetchCountries,
  filterCountriesByCurrency,
} from "@/app/services/localservices/countryService";
import { COUNTRY_BASE_PATH } from "@/app/utils/config";
import { useSelector } from "react-redux";
import type { RootState } from "@/app/redux/store";

const DEFAULT_CURRENCIES = ["USD", "EUR", "AED", "SAR", "INR"];

const FAMOUS_COUNTRY_CURRENCIES = new Set([
  "USD", "EUR", "GBP", "JPY", "CNY", "CHF", "CAD", "AUD", "INR", "BRL",
  "RUB", "KRW", "MXN", "IDR", "TRY", "SAR", "ZAR", "SGD", "HKD", "AED",
  "SEK", "NOK", "DKK", "PLN", "NZD", "THB", "MYR", "PHP", "VND", "EGP",
  "ILS", "NGN", "PKR", "TWD",
]);

const PREFERRED_COUNTRY_CODES: Record<string, string> = {
  USD: "US", EUR: "ES", GBP: "GB", JPY: "JP", AED: "AE", SAR: "SA",
  INR: "IN", CNY: "CN", CHF: "CH", CAD: "CA", AUD: "AU", BRL: "BR",
  RUB: "RU", KRW: "KR", MXN: "MX", SGD: "SG", HKD: "HK", SEK: "SE",
  NOK: "NO", DKK: "DK", PLN: "PL", NZD: "NZ", THB: "TH", MYR: "MY",
  PHP: "PH", VND: "VN", IDR: "ID", TRY: "TR", ZAR: "ZA", EGP: "EG",
  ILS: "IL", NGN: "NG", PKR: "PK", TWD: "TW",
};

const fetchBalanceData = async (currencyCode: string) => {
  const response = await gxAccountBalanceTotalApi({
    currencyCode: currencyCode,
  });

  if (response?.status === 1 && response.data) {
    return response.data;
  }

  throw new Error(
    response?.errorMessage ??
      response?.errorCode ??
      "Failed to fetch balance data",
  );
};

// Filter to only famous/major countries
const filterFamousCountries = (countries: Country[]): Country[] => {
  return countries.filter((country) =>
    FAMOUS_COUNTRY_CURRENCIES.has(country.currency.code), 
  );
};


const getUniqueCurrencies = (countries: Country[]): Country[] => {
  const currencyMap = new Map<string, Country>();

  countries.forEach((country) => {
    const currencyCode = country.currency.code;

    if (currencyMap.has(currencyCode)) {
      const preferredCode = PREFERRED_COUNTRY_CODES[currencyCode];
      if (preferredCode && country.code === preferredCode) {
        currencyMap.set(currencyCode, country);
      }
    } else {
      currencyMap.set(currencyCode, country);
    }
  });

  return Array.from(currencyMap.values());
};

const sortCountriesAlphabetically = (countries: Country[]): Country[] => {
  return [...countries].sort((a, b) => {
    return a.currency.name.localeCompare(b.currency.name);
  });
};

const fetchBankCurrencies = async (): Promise<string[]> => {
  try {
    const response = await gxAccountBankCurrencyApi();

    if (
      response?.status === 1 &&
      response.data &&
      Array.isArray(response.data)
    ) {
      const currencyCodes = response.data
        .map((item) => item.currencyCode?.toUpperCase())
        .filter((code): code is string => Boolean(code));

      return currencyCodes.length > 0 ? currencyCodes : DEFAULT_CURRENCIES;
    }

    console.warn("API returned unsuccessful response, using defaults");
    return DEFAULT_CURRENCIES;
  } catch (err) {
    console.error("Error fetching bank currencies:", err);
    return DEFAULT_CURRENCIES;
  }
};

const CountrySelector: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedCountry, setSelectedCountry } = useCountry();
  const userData = useSelector((state: RootState) => state.userData.data);
  

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [searchItems, setSearchItems] = useState<Country[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement | null>(null);

  // Initialize countries
  useEffect(() => {
    let isMounted = true;

    const initializeCountries = async () => {
      setLoading(true);
      setError(null);

      try {
        const [allCountries, allowedCurrencyCodes] = await Promise.all([
          fetchCountries(),
          fetchBankCurrencies(),
        ]);

        if (!isMounted) return;

        const filteredCountries = filterCountriesByCurrency(
          allCountries,
          allowedCurrencyCodes,
        );

        if (filteredCountries.length === 0) {
          throw new Error("No countries match the allowed currencies");
        }

        const famousCountries = filterFamousCountries(filteredCountries);
        const uniqueCurrencies = getUniqueCurrencies(famousCountries);
        const sortedCountries = sortCountriesAlphabetically(uniqueCurrencies);

        setCountries(sortedCountries);
        setSearchItems(sortedCountries);
      } catch (err) {
        console.error("Error initializing countries:", err);
        if (isMounted) {
          setError(
            err instanceof Error ? err.message : "Failed to load countries",
          );
          setCountries([]);
          setSearchItems([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    initializeCountries();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedCountry && countries.length > 0) {
      let defaultCountry: Country | undefined;

      if (userData?.currencyCode) {
        defaultCountry = countries.find(
          (country) => country.currency.code === userData?.currencyCode,
        );
      }

      if (!defaultCountry && userData?.countryCode2) {
        defaultCountry = countries.find(
          (country) => country.code === userData?.countryCode2,
        );
      }

      defaultCountry ??= countries.find((country) => country.currency.code === "EUR") ?? countries[0];

      setSelectedCountry(defaultCountry);
    }
  }, [
    selectedCountry,
    countries,
    setSelectedCountry,
    userData?.countryCode2,
    userData?.currencyCode,
  ]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
        setSearchValue("");
        setSearchItems(countries);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen, countries]);

  const toggleDropdown = () => {
    setDropdownOpen((prev) => {
      const newState = !prev;
      if (newState) {
        setSearchValue("");
        setSearchItems(countries);
      }
      return newState;
    });
  };

  const handleCountrySelect = (item: Country) => {
    setSelectedCountry(item);
    setDropdownOpen(false);
    setSearchValue("");
    setSearchItems(countries);
  };

  // Prefetch on hover using React Query
  const handleCountryHover = (item: Country) => {
    if (item.currency.code !== selectedCountry?.currency?.code) {
      queryClient.prefetchQuery({
        queryKey: ["balance", item.currency.code],
        queryFn: () => fetchBalanceData(item.currency.code),
        staleTime: 5 * 60 * 1000,
      });
    }
  };

  const searchCountry = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    if (!value.trim()) {
      setSearchItems(countries);
      return;
    }

    const lowerValue = value.toLowerCase();
    const filtered = countries.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerValue) ||
        item.currency.code.toLowerCase().includes(lowerValue) ||
        item.currency.name?.toLowerCase().includes(lowerValue),
    );

    const sortedFiltered = sortCountriesAlphabetically(filtered);
    setSearchItems(sortedFiltered);
  };

  if (loading) {
    return (
      <div className="gx-cs-container">
        <div className="gx-cs-trigger" style={{ opacity: 0.6 }}>
          <div
            className="gx-cs-flag"
            style={{
              width: "20px",
              height: "15px",
              backgroundColor: "#f0f0f0",
              borderRadius: "2px",
            }}
          />
          <span className="gx-cs-currency-code gx-font-bold">...</span>
          <svg
            className="gx-cs-arrow"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    );
  }

  if (error || countries.length === 0) {
    return (
      <div className="gx-cs-container">
        <div className="gx-cs-trigger" style={{ opacity: 0.6 }}>
          <span className="gx-cs-currency-code" style={{ fontSize: "12px" }}>
            {error ? "Error" : t("no_countries")}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="gx-cs-container" ref={dropdownRef}>
      <button
        type="button"
        className="gx-cs-trigger"
        onClick={toggleDropdown}
        style={{ opacity: 0.8, cursor: "pointer" }}
      >
        {selectedCountry ? (
          <>
            <img
              className="gx-cs-flag"
              src={`${COUNTRY_BASE_PATH}${selectedCountry.flagIcon}`}
              alt={`${selectedCountry.name} flag`}
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
            <span className="gx-cs-currency-code gx-font-bold">
              {selectedCountry.currency.code}
            </span>
          </>
        ) : (
          <span className="gx-cs-currency-code gx-font-bold">
            {t("select")}
          </span>
        )}
        <svg
          className="gx-cs-arrow"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          style={{
            transform: dropdownOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {dropdownOpen && (
        <div className="gx-cs-dropdown">
          <div className="gx-cs-search-wrapper">
            <input
              onChange={searchCountry}
              value={searchValue}
              type="text"
              className="gx-cs-search-input"
              placeholder="Search country or currency..."
              autoFocus
            />
          </div>

          <div
            className="gx-cs-list-container"
            style={{ maxHeight: "200px", overflowY: "auto" }}
          >
            {searchItems.length > 0 ? (
              searchItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className="gx-cs-item hover:bg-blue-600 hover:text-white"
                  onClick={() => handleCountrySelect(item)}
                  onMouseEnter={() => handleCountryHover(item)}
                  style={{
                    backgroundColor:
                      selectedCountry?.id === item.id ? "#e3f2fd" : undefined,
                  }}
                >
                  <img
                    src={`${COUNTRY_BASE_PATH}${item.flagIcon}`}
                    alt={`${item.name} flag`}
                    className="gx-cs-item-flag"
                    onError={(e) => {
                      if (item.fallbackicon) {
                        e.currentTarget.src = item.fallbackicon;
                        e.currentTarget.style.visibility = "visible";
                      } else {
                        e.currentTarget.style.visibility = "hidden";
                      }
                    }}
                  />
                  <div className="gx-cs-item-content">
                    <span className="gx-cs-item-currency">
                      {item.currency.code} - {item.currency.name}
                    </span>
                  </div>
                </button>
              ))
            ) : (
              <div className="gx-cs-not-found">
                No countries found matching &quot;{searchValue}&quot;
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CountrySelector;